import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import SortableGrid from "../components/SortableGrid";
import api from "../lib/api";
import { fmtCur, fmtDate, fmtNum } from "../lib/format";
import { ExportXlsxButton } from "../components/ExportXlsxButton";
import { xlsxDate } from "../lib/exportXlsx";

// Color per non-commercial invoice type (kept distinct from the commercial Shipping table).
const TYPE_COLORS = {
  "Sample Inv": ["#EDE9FE", "#5B21B6"],
  "Tariff Inv": ["#DBEAFE", "#1E40AF"],
  Freight: ["#DCFCE7", "#166534"],
  Surcharge: ["#FEE2E2", "#991B1B"],
  "Inv.Carry": ["#FEF3C7", "#92400E"],
  "Re-Work": ["#F1F5F9", "#475569"],
};

const TypePill = ({ t }) => {
  const [bg, fg] = TYPE_COLORS[t] || ["#F1F5F9", "#475569"];
  return (
    <span style={{ display: "inline-block", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: bg, color: fg, whiteSpace: "nowrap" }}>
      {t || "—"}
    </span>
  );
};

// "USD 104,008.00 · INR 5,138.00" for the amount-by-currency roll-up.
const amountByCur = (map) =>
  Object.entries(map || {})
    .filter(([, v]) => Number(v))
    .map(([c, v]) => fmtCur(v, c))
    .join(" · ") || "—";

export default function OtherCharges() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get("/shipping/other-charges")
      .then((r) => { setRows(r.data?.rows || []); setSummary(r.data?.summary || []); })
      .catch((e) => setError(e.response?.data?.detail || e.message || "Failed to load other charges"))
      .finally(() => setLoading(false));
  }, []);

  const totalCount = useMemo(() => rows.length, [rows]);

  // Finance export: full invoice list, sorted by charge type then newest date (groupable per type).
  const exportRows = useMemo(
    () => [...rows].sort((a, b) => (a.inv_type || "").localeCompare(b.inv_type || "")
      || (b.inv_date || "").localeCompare(a.inv_date || "")),
    [rows]);
  const exportColumns = [
    { header: "Charge Type", value: (r) => r.inv_type || "" },
    { header: "Invoice #", value: (r) => r.inv_no || "" },
    { header: "Date", value: (r) => xlsxDate(r.inv_date) },
    { header: "Customer", value: (r) => r.party_name || r.pcode || "" },
    { header: "Customer Code", value: (r) => r.pcode || "" },
    { header: "Plant", value: (r) => r.comp_code || "" },
    { header: "Cartons", value: (r) => Number(r.cartons) || 0 },
    { header: "PO #(s)", value: (r) => r.po_nos || "" },
    { header: "Amount", value: (r) => Number(r.amount) || 0 },
    { header: "Currency", value: (r) => r.currency || "" },
  ];

  const summaryColumns = [
    { key: "inv_type", header: "Charge Type", accessor: (s) => s.inv_type,
      render: (s) => <TypePill t={s.inv_type} /> },
    { key: "count", header: "Invoices", align: "right", accessor: (s) => s.count,
      render: (s) => <span className="text-mono" style={{ fontWeight: 700 }}>{s.count}</span> },
    { key: "cartons", header: "Cartons", align: "right", accessor: (s) => s.cartons,
      render: (s) => <span className="text-mono">{s.cartons ? fmtNum(s.cartons) : "—"}</span> },
    { key: "amount", header: "Amount", align: "right", accessor: (s) => Object.values(s.amount_by_currency || {}).reduce((a, b) => a + Number(b || 0), 0),
      render: (s) => <span className="text-mono" style={{ fontWeight: 700, fontSize: 12 }} data-testid={`oc-amount-${s.inv_type}`}>{amountByCur(s.amount_by_currency)}</span> },
  ];

  const renderExpanded = (typeRow) => {
    const invs = rows.filter((r) => r.inv_type === typeRow.inv_type);
    return (
      <div data-testid={`oc-drill-${typeRow.inv_type}`} style={{ padding: "8px 12px", background: "#FAFAFA" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "#64748B", borderBottom: "1px solid #E2E8F0" }}>
              <th style={{ padding: "6px 8px" }}>Invoice #</th>
              <th style={{ padding: "6px 8px" }}>Date</th>
              <th style={{ padding: "6px 8px" }}>Customer</th>
              <th style={{ padding: "6px 8px", textAlign: "right" }}>Cartons</th>
              <th style={{ padding: "6px 8px" }}>PO #(s)</th>
              <th style={{ padding: "6px 8px", textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {invs.map((iv) => (
              <tr key={iv.inv_no} data-testid={`oc-inv-${iv.inv_no}`} style={{ borderBottom: "1px solid #F1F5F9" }}>
                <td style={{ padding: "6px 8px", fontWeight: 700 }} className="text-mono">{iv.inv_no}</td>
                <td style={{ padding: "6px 8px" }} className="text-mono">{fmtDate(iv.inv_date)}</td>
                <td style={{ padding: "6px 8px" }}>
                  {iv.party_name || <span style={{ color: "#94A3B8" }}>{iv.pcode || "—"}</span>}
                </td>
                <td style={{ padding: "6px 8px", textAlign: "right" }} className="text-mono">{iv.cartons ? fmtNum(iv.cartons) : "—"}</td>
                <td style={{ padding: "6px 8px", color: "#64748B" }} className="text-mono">{iv.po_nos || "—"}</td>
                <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700 }} className="text-mono">{fmtCur(iv.amount, iv.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Layout breadcrumb={["Home", "Other Charges & Invoices"]}>
      <div className="jde-page" data-testid="other-charges-page">
        <div className="jde-panel">
          <div className="jde-panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div className="jde-panel-title">
              OTHER CHARGES &amp; INVOICES ({totalCount}{loading ? "…" : ""})
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 600 }}>
                Non-commercial invoice types (excluded from Shipping) · last 120 days
              </span>
              <ExportXlsxButton filename="other_charges_invoices" columns={exportColumns}
                rows={exportRows} sheetName="Other Charges" testid="oc-export-xlsx" />
            </div>
          </div>

          {loading && <div style={{ padding: 24, color: "#64748B" }} data-testid="oc-loading">Loading…</div>}
          {error && <div style={{ padding: 16, color: "#B91C1C" }} data-testid="oc-error">{error}</div>}

          {!loading && !error && (
            <SortableGrid pageSize={100}
              testid="oc-summary"
              columns={summaryColumns}
              rows={summary}
              getRowKey={(s) => s.inv_type}
              defaultSort={{ key: "count", dir: "desc" }}
              renderExpanded={renderExpanded}
              searchPlaceholder="Search charge type…"
              emptyMessage="No other charges or non-commercial invoices in the last 120 days."
              statusBar={(vis) => `${vis.length} charge type(s) · ${totalCount} invoice(s)`}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}
